import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { RequestDeal } from '@api/models/request.model';
import { RequestsService } from '@api/services/requests.service';
import { CardComponent } from '@ui/card/card.component';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ImageViewerComponent } from '@ui/image-viewer/image-viewer.component';
import { IconComponent } from '@ui/icon/icon.component';
import { ButtonComponent } from '@ui/button/button.component';

@Component({
  selector: 'app-requests-deals',
  standalone: true,
  imports: [CardComponent, CommonModule, ImageViewerComponent, IconComponent, ButtonComponent],
  template: `<ui-card [title]="cardTitle">
    @if (isLoading()) {
    <div class="flex justify-center items-center p-4">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
      ></div>
    </div>
    } @else { @if (deal) {
    <div class="flex flex-row gap-2 justify-between mb-4">
      <div class="text-sm font-medium">{{ deal.offer_number }}</div>
      <div class="text-sm text-gray-500">
        {{ deal.offer_date | date : 'dd-MM-yyyy' }}
      </div>
    </div>

    <!-- Foto's sectie -->
    @if (hasImages()) {
      <div class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Foto's</h4>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          @for (image of [ { data: deal.image_situation, label: 'Situatie' }, {
          data: deal.image_above, label: 'Foto boven' }, { data: deal.image_under,
          label: 'Foto onder' }, { data: deal.image_opnameblad, label: 'Opnameblad'
          }, { data: deal.image_overview, label: 'Overzicht' }, { data:
          deal.image_extra, label: 'Extra' }, { data: deal.image_digital_drawing, label: 'Digitale tekening' } ]; track image.label) { @if
          (image.data) {

          <div class="relative aspect-video mb-4">
            <img
              [src]="getImageUrl(image.data)"
              (click)="openImage(image.data)"
              class="rounded-lg object-cover w-full h-full cursor-pointer hover:opacity-90 transition-opacity"
              [alt]="image.data.filename"
            />
          </div>

          } }
        </div>
      </div>
    }

    <!-- Bestanden sectie -->
    @if (deal.files && deal.files.length > 0) {
      <div>
        <h4 class="text-sm font-medium text-gray-700 mb-3">Bestanden</h4>
        <div>
          @for (file of deal.files; track file.file_id) {
            <div class="flex items-center justify-between p-3 pr-0 hover:bg-gray-50 rounded transition-colors group cursor-pointer">
              <div class="flex items-center gap-3">
                <ui-icon
                  [name]="getFileIcon(file?.filename || '')"
                  class="text-gray-500 w-5 h-5"
                ></ui-icon>
                <div>
                  <div class="font-medium text-sm">{{ file?.filename }}</div>
                  <div class="text-xs text-gray-500">{{ file?.field_name }}</div>
                </div>
              </div>
              <ui-button
                variant="secondary"
                size="sm"
                class="pr-0"
                (click)="downloadFile(file?.file_id || '', file?.filename || '')"
                [disabled]="downloadingFiles().has(file?.file_id || '')"
              >
                @if (downloadingFiles().has(file?.file_id || '')) {
                  <ui-icon name="spinner" class="animate-spin w-4 h-4"></ui-icon>
                } @else {
                  <ui-icon name="download" class="w-4 h-4"></ui-icon>
                }
                Download
              </ui-button>
            </div>
          }
        </div>
      </div>
    }
    } }

    @if (showImageViewer()) {
      <app-image-viewer
        [images]="getViewerImages()"
        [initialIndex]="currentImageIndex()"
        (close)="closeViewer()"
      />
    }
  </ui-card>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsDealsComponent implements OnInit {
  @Input() deal: RequestDeal | null = null;
  @Input() requestId: string | null = null;
  requestsService = inject(RequestsService);
  private imageUrls = new Map<string, string>();
  isLoading = signal(true);
  showImageViewer = signal(false);
  currentImageIndex = signal(0);
  downloadingFiles = signal(new Set<string>());
  cardTitle =  'Offerte';
  ngOnInit() {
    this.preloadAllImages();
  }

  preloadAllImages() {
    if (!this.requestId || !this.deal) return;

    const imageRequests: Array<Observable<any>> = [];
    const uniqueImages = new Set<string>();

    const images = [
      this.deal?.image_situation,
      this.deal?.image_above,
      this.deal?.image_under,
      this.deal?.image_opnameblad,
      this.deal?.image_overview,
      this.deal?.image_extra,
      this.deal?.image_digital_drawing,
    ].filter((img) => img !== null && img !== undefined);

    images.forEach((image) => {
      if (image && !uniqueImages.has(image.file_id)) {
        uniqueImages.add(image.file_id);
        imageRequests.push(
          this.requestsService
            .getRequestDealImages(this.requestId!, image.file_id)
            .pipe(map((response) => ({ response, fileId: image.file_id })))
        );
      }
    });

    forkJoin(imageRequests).subscribe({
      next: (results) => {
        results.forEach(({ response, fileId }) => {
          const blob = new Blob([response], { type: 'image/jpeg' });
          const imageUrl = URL.createObjectURL(blob);
          this.imageUrls.set(fileId, imageUrl);
        });
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading images:', error);
        this.isLoading.set(false);
      },
    });
  }

  getImageUrl(image: RequestDeal['image_situation']): string | null {
    return image ? this.imageUrls.get(image.file_id) || null : null;
  }

  ngOnDestroy() {
    this.imageUrls.forEach((url) => URL.revokeObjectURL(url));
    this.imageUrls.clear();
  }

  getViewerImages() {
    if (!this.deal) return [];

    return [
      { data: this.deal.image_situation, label: 'Situatie' },
      { data: this.deal.image_above, label: 'Foto boven' },
      { data: this.deal.image_under, label: 'Foto onder' },
      { data: this.deal.image_opnameblad, label: 'Opnameblad' },
      { data: this.deal.image_overview, label: 'Overzicht' },
      { data: this.deal.image_extra, label: 'Extra' },
      { data: this.deal.image_digital_drawing, label: 'Digitale tekening' }
    ]
      .filter(item => item.data)
      .map(item => ({
        url: this.getImageUrl(item.data)!,
        label: item.label
      }));
  }

  openImage(image: RequestDeal['image_situation']) {
    if (!this.requestId || !image) return;

    const images = this.getViewerImages();
    const index = images.findIndex(img =>
      this.getImageUrl(image) === img.url
    );

    if (index !== -1) {
      this.currentImageIndex.set(index);
      this.showImageViewer.set(true);
    }
  }

  closeViewer() {
    this.showImageViewer.set(false);
  }

  hasImages(): boolean {
    if (!this.deal) return false;

    const images = [
      this.deal.image_situation,
      this.deal.image_above,
      this.deal.image_under,
      this.deal.image_opnameblad,
      this.deal.image_overview,
      this.deal.image_extra,
      this.deal.image_digital_drawing,
    ];

    return images.some(image => image !== null && image !== undefined);
  }

  getFileIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      case 'xls':
      case 'xlsx':
        return 'file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'file-image';
      case 'zip':
      case 'rar':
      case '7z':
        return 'file-archive';
      default:
        return 'file';
    }
  }

  downloadFile(fileId: string, filename: string) {
    if (!this.requestId) return;

    const currentDownloading = this.downloadingFiles();
    currentDownloading.add(fileId);
    this.downloadingFiles.set(new Set(currentDownloading));

    this.requestsService.getRequestDealFile(this.requestId, fileId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        const currentDownloading = this.downloadingFiles();
        currentDownloading.delete(fileId);
        this.downloadingFiles.set(new Set(currentDownloading));
      },
      error: (error) => {
        console.error('Error downloading file:', error);
        const currentDownloading = this.downloadingFiles();
        currentDownloading.delete(fileId);
        this.downloadingFiles.set(new Set(currentDownloading));
      },
    });
  }
}
